import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { Observable, Subscription, throwError, combineLatest, of, iif } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { RoutesService } from '../../../../shared/services/routes/routes.service';
import {
  ErrorHandlerStrategy,
  filterUndefined,
  HydraCollection,
  Professional,
  ProfessionalService,
  ProfessionalStyle, WebServiceOptions, WebServiceSubResourceFetchMode
} from '@adeo/ngx-kozikaza-api';
import {
  ProfessionalServiceRessource,
  ProfessionalServiceService,
  ProfessionalServiceType,
} from '@adeo/ngx-kozikaza-api/pro/service';
import { HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { SeoService } from '../../../../shared/services/utilities/seo.service';
import { getTranslation } from '../../../../utils/const/translation';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { boxSlug, decoOffersSlug } from '../professional-routing.module';
import { UserStoreService } from '../../../../shared/services/state-management/user-store.service';
import { KzIconsRegistryService } from '../../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsLike } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-like.icon';
import { kzIconsBad } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-bad.icon';
import { TranslateService } from '@ngx-translate/core';
import { ProTenderService } from '../pro-tender/pro-tender.service';
import { ProfessionalFavorite, ProfessionalFavoriteService } from '@adeo/ngx-kozikaza-api/pro/misc';
import { ProUsersService } from 'src/app/shared/services/pro/pro-users.service';
@Component({
  selector: 'app-listing',
  templateUrl: './pro-listing.component.html',
  styleUrls: ['./pro-listing.component.scss'],
})
export class ProListingComponent implements OnInit, OnDestroy {
  private routerSubscription: Subscription;

  proListingLoading = true;
  proListing: Array<Professional>;
  proServiceListing: Array<ProfessionalServiceRessource>;

  // Pagination
  page = 1;
  maxPage = 1;
  itemsPerPage = 10;
  itemsPerPageValues = [10, 20];

  jobPath: 'interior-decorator' | 'interior-architect';
  boxPath: Array<string> = [];
  servicesPath: Array<string> = [];
  styleSlug: string;
  brand: string;
  style: ProfessionalStyle;
  city: string;
  cityLabel: string;
  serviceTypeSlug: string;
  jobFilter: string;
  serviceType: ProfessionalServiceType;
  geo: { lat: string; lon: string};
  geoLabel: string;
  distance= 50000;
  boxFiltered = false;
  displayServices = false;
  sort: string;
  favorites = false;
  atHome: boolean;

  private _randomizedListingProSeed: string;
  private refreshSubscription: Subscription;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly routesService: RoutesService,
    private readonly proService: ProfessionalService,
    private readonly proServiceService: ProfessionalServiceService,
    private readonly router: Router,
    private readonly cookieService: CookieService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly seoService: SeoService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(TransferState) private transferState: TransferState,
    private readonly userStoreService: UserStoreService,
    private readonly kzIconsRegistryService: KzIconsRegistryService,
    private readonly translateService: TranslateService,
    private readonly proTenderService: ProTenderService,
    private professionalFavoriteService: ProfessionalFavoriteService,
    private proUsersService: ProUsersService
  ) {
    this.kzIconsRegistryService.registerIcons([kzIconsLike, kzIconsBad]);
    // Initializing a seed to handle the randomization of the listing
    this._randomizedListingProSeed = Math.random().toString();
    if (isPlatformServer(this.platformId)) {
      const tmpData = JSON.parse(JSON.stringify(this._randomizedListingProSeed));
      this.transferState.set(makeStateKey('_randomizedListingProSeed'), tmpData);
    } else {
      this._randomizedListingProSeed = this.transferState.get(makeStateKey('_randomizedListingProSeed'), this._randomizedListingProSeed);
    }
  }

  ngOnInit(): void {
    this.routerSubscription = combineLatest([
      this.userStoreService.userStore.pipe(
        filterUndefined(),
      ),
      this.routesService.eventNavigationEnd.pipe(
        filter((route) => route.id === 1 || !this.routesService.routeHasAnOutlet()),
        distinctUntilChanged((prev, curr) => prev.url === curr.url),
        map(() => this.activatedRoute.snapshot),
        tap((route: any) => {

          if (route.queryParams.tenders && isPlatformBrowser(this.platformId)) {
            this.router.navigate(['.'],{ relativeTo: this.activatedRoute.parent, queryParams: { tenders: null}, queryParamsHandling: 'merge'}).then(() => {
              this.proTenderService.openTenderRequest(route.queryParams.tenders !== 'true' ? route.queryParams.tenders : null);
            });

          }

          this.itemsPerPage = (route.queryParams?.itemsPerPage) ? parseInt(route.queryParams.itemsPerPage, 10) : 10;
          this.page = (route.params?.page && !isNaN(parseInt(route.params.page, 10))) ? parseInt(route.params.page, 10) : 1;
          this.styleSlug = (route.params?.styleSlug) ? route.params.styleSlug : null;
          this.style = (route.parent.parent.data?.style) ? route.parent.parent.data?.style : null;
          this.city = (route.parent.parent.params?.citySlug) ? route.parent.parent.params?.citySlug : null;
          this.cityLabel = (route.parent.parent.params?.city) ? route.parent.parent.params?.city : null;
          this.serviceTypeSlug = (route.params?.serviceTypeSlug) ? route.params.serviceTypeSlug : null;
          this.serviceType = (route.parent.parent.data?.serviceType) ? route.parent.parent.data?.serviceType : null;
          this.boxFiltered = !!route.data.isBox;
          this.displayServices = !!route.data.isDecoOffers;
          this.boxPath = this.boxFiltered ? [boxSlug] : [];

          const urlTree = this.router.parseUrl(this.router.url);
          const urlSegments = urlTree.root.children.primary.segments;
          urlSegments.map(it => it.path).filter((seg) => {
            if (seg == this.translateService.instant('routes.interior-decorator')) {
              this.jobPath = 'interior-decorator';
              this.jobFilter = environment.PRO.DECORATOR_JOB;
              return true;
            }
            if (seg == this.translateService.instant('routes.interior-architect')) {
              this.jobPath = 'interior-architect';
              this.jobFilter = environment.PRO.ARCHITECT_JOB;
              return true;
            }
            return false;
          });

          this.servicesPath = this.displayServices ? [decoOffersSlug] : [];

          const paramsMetas: {styleName?: string; serviceTypeName?: string; cityName?: string;} = {};
          if (this.style) {
            paramsMetas.styleName = this.style.name;
          }

          if (route.queryParams && route.queryParams.atHome) {
            this.atHome = route.queryParams.atHome;
          }

          if (this.serviceType) {
            paramsMetas.serviceTypeName = this.serviceType.name;
          }

          if (this.city) {
            paramsMetas.cityName = this.cityLabel;

            const geoQueryParams = route.params.cityGeo.split('_');
            this.geo = {
              lat: geoQueryParams[1],
              lon: geoQueryParams[2]
            }
            this.geoLabel = decodeURI(geoQueryParams[0]);
          }

          this.seoService.setMetas({
            ...route.data.metas, ...{
              title: getTranslation(route.data.metas.titleKey, {...paramsMetas}),
              description: getTranslation(route.data.metas.descriptionKey, {...paramsMetas})
            }
          });

          if (route.queryParams && route.queryParams.geo) {
            const geoQueryParams = route.queryParams.geo.split('_');
            this.geo = {
              lat: geoQueryParams[1],
              lon: geoQueryParams[2]
            }
            this.geoLabel = decodeURI(geoQueryParams[0]);
          }

          if (!this.city && !route.queryParams.geo) {
            this.geo = null;
            this.geoLabel = '';
          }

          if (route.queryParams?.favorites) {
            this.favorites = true;
          } else {
            this.favorites = false;
          }

          if (route.queryParams?.sort) {
            this.sort = decodeURI(route.queryParams.sort);
          } else {
            this.sort = null;
          }

          if (route.queryParams?.brand) {
            this.brand = decodeURI(route.queryParams.brand);
          } else {
            this.brand = null;
          }
        }))
    ])
      .pipe(switchMap(() => this.loadList()))
      .subscribe();
  }

  refreshList(): void {
    if (this.favorites) {
      this.refreshSubscription = this.loadList().subscribe(() => this.refreshSubscription.unsubscribe());
    }
  }

  getOtherSearch(): Array<string> {
    if (this.style || this.serviceType) {
      if (this.geo) {
        return this.localizeRouterService.translateRoute(['/', 'pro', ...[this.jobPath], ...this.servicesPath, ...this.boxPath,  `${this.displayServices ? 'type-' + this.serviceTypeSlug : 'style-' + this.styleSlug}`]) as string[];
      } else {
        return this.localizeRouterService.translateRoute(['/', 'pro', ...[this.jobPath],...this.servicesPath, ...this.boxPath ]) as string[];
      }
    } else {
      return this.localizeRouterService.translateRoute(['/', 'pro', ...[this.jobPath]]) as string[];
    }
  }

  getFavorites(data: any): Observable<HydraCollection<ProfessionalFavorite>> {
    return this.userStoreService.userStore
      .pipe(
        switchMap((user) => {
          return iif(() => !!user,
            this.proUsersService.getAPIProUserIdFromKzkzUser(user, true)
              .pipe(
                filter((userPro) => !!userPro),
                switchMap((userProId: string) => {
                  const webServiceOptions: WebServiceOptions = {
                    subResourceFetchMode: WebServiceSubResourceFetchMode.SYNC,
                    errorHandlerStrategy: ErrorHandlerStrategy.Ignore,
                    refresh: true,
                    query: new HttpParams({
                      fromObject: {
                        'professional[]': data['hydra:member'].map((p) => p['@id']),
                        author: userProId,
                        refresh: `${new Date().getTime()}`,
                      }
                    })
                  };

                  return this.professionalFavoriteService.getProfessionalFavorites(null, webServiceOptions);
                }),
                switchMap((favorites: HydraCollection<ProfessionalFavorite>) => {
                  const proFavorites = {
                    ...data,
                    'hydra:member': data['hydra:member'].map((p) => {
                      return {
                        ...p,
                        favoriteId: favorites['hydra:member'].find((f) => f['professional'] === p['@id'])?.['@id']
                      }
                    })
                  };

                  return of(proFavorites);
                })
              )
            ,
            of(true).pipe(
              switchMap(() => {
                const proFavorites = {
                  ...data,
                  'hydra:member': data['hydra:member'].map((p) => {
                    return {
                      ...p,
                      favoriteId: null
                    }
                  })
                };

                return of(proFavorites);
              })
            )
          )
        })
      );
  }

  loadList(): Observable<HydraCollection<Professional | ProfessionalServiceRessource>> {
    this.proListingLoading = true;

    let params = new HttpParams({
      fromObject: {
        page: `${ this.page }`,
        itemsPerPage: this.itemsPerPage.toString(),
      },
    });

    // isCertified
    params = params.append(`${this.displayServices ? 'pro.isCertified' : 'isCertified' }`, 'true');

    // styles
    if (this.styleSlug) {
      params = params.append('styles', this.style['@id']);
    }


    // search
    if (this.brand) {
      params = params.append('brand', this.brand);
    }

    // service types
    if (this.serviceTypeSlug) {
      params = params.append('types', this.serviceType['@id']);
    }

    //Favorites
    if (this.favorites && this.userStoreService.user) {

      params = params.append('favorites.author.reach5Id', this.userStoreService.user.reach5Id);
    }

    if (!!this.atHome) {
      params = params
        .append('coachAtHome', this.atHome)
    }

    // geolocation
    if (this.geo) {
      params = params
        .append('geo[lat]', this.geo.lat)
        .append('geo[lon]', this.geo.lon)
        .append('geo[dist]', this.distance.toString());
    } else {
      if (!this.sort) {
        params = params
          .append('order[pertinencePoints]', 'DESC');
      }
    }

    // Sort
    if (this.sort && this.displayServices) {
      params = params.append(`order[priceNumber]`, this.sort);
      params = params.append(`priceNumber[gt]`, 0);
    }

    // box offers
    if (this.boxFiltered) {
      params = params.append(`${this.displayServices ? 'boxable' : 'services.boxable' }`, 'true');
    }

    if (this.jobFilter) {
      params = params.append(`${this.displayServices ? 'pro.mainJob[]' : 'mainJob[]'}`, this.jobFilter);
    }

    const listingRequest: Observable<HydraCollection<Professional | ProfessionalServiceRessource>> = this.displayServices
      ? this.proServiceService.getProfessionalServices([{professional: ['mainJob']}, 'types'], {query: params, refresh: true, refreshOnlyFirstLevel: true})
      : this.proService.getProfessionals(['mainJob', 'styles'], {query: params, refresh: true, refreshOnlyFirstLevel: true});

    return listingRequest
      .pipe(
        catchError((err) => {
          return throwError(err);
        }),
        finalize(() => {
          this.proListingLoading = false;
          this.cdr.detectChanges();
        }),
        switchMap((data: HydraCollection<Professional | ProfessionalServiceRessource>) => {
          if (this.displayServices) {
            return of(data);
          } else {
            return this.getFavorites(data);
          }
        }),
        tap((data: HydraCollection<Professional | ProfessionalServiceRessource>) => {
          /*
            if (this.geo && collection['hydra:totalItems'] == 0){
            params = params.set('geo[dist]','1500000');
          }*/

          if (data['hydra:totalItems'] < 5) {
            this.seoService.initFollow('noindex, nofollow');
          }

          if (this.displayServices) {
            this.proServiceListing = data['hydra:member'] as Array<ProfessionalServiceRessource>;
          } else {
            this.proListing = data['hydra:member'] as Array<Professional>;
          }

          this.maxPage = Math.ceil((data['hydra:totalItems'] / this.itemsPerPage));
          this.cdr.detectChanges();
        })
      );
  }

  ngOnDestroy(): void {
    if (this.routerSubscription && !this.routerSubscription.closed) {
      this.routerSubscription.unsubscribe();
    }
  }

  changeItemPerPage(nb: number): void {
    if(nb !== this.itemsPerPage) {
      const route = this.generateRoutePagination(this.page);
      this.router.navigate(this.localizeRouterService.translateRoute(route) as string[], {queryParams: {itemsPerPage: nb}});
    }
  }

  get prevPage(): Array<string> {
    const route = this.generateRoutePagination(this.page, 'prev');
    return this.localizeRouterService.translateRoute(route) as string[];
  }

  get nextPage(): Array<string> {
    const route = this.generateRoutePagination(this.page, 'next');
    return this.localizeRouterService.translateRoute(route) as string[];
  }

  generateRoutePagination(page = 1, way = null) {
    const route = ['/', 'pro']


    if (this.jobPath) {
      route.push(this.jobPath);
    }

    if (this.displayServices) {
      route.push(decoOffersSlug);
    }
    if (this.boxFiltered) {
      route.push(boxSlug);
    }
    if (this.styleSlug) {
      route.push('style-' + this.styleSlug);
    }
    if (this.serviceTypeSlug) {
      route.push('type-' + this.serviceTypeSlug);
    }

    if ((this.page - 1) !== 1 && way === 'prev') {
      route.push('page', (this.page - 1).toString());
    }

    if ((this.page + 1) !== 1 && way === 'next') {
      route.push('page', (this.page + 1).toString());
    }

    return route;
  }

  trackById(index, data): number {
    return data ? data['@id'] : undefined;
  }
}
